/**
 * Browser tasks (AMO-155): a browser job written down so a workflow's
 * Browser task step can do it again. The Workflows page lists the viewer's
 * own tasks and the organization's; a task opens in a dialog that edits it
 * (or shows it, to someone who may not change it).
 *
 * A task the agent drafted from a chat is a draft until its owner reviews
 * it: the dialog's main button is then "Save and use", which makes it ready.
 * Saving names the version it was edited from; a colleague's newer save is
 * loaded in its place, with the server's sentence saying so.
 *
 * @module components/workflows/BrowserTasks
 */
import type { BrowserTask, BrowserTaskScope, BrowserTaskSummary } from "@amoreg/contracts";
import { ChevronRightIcon, GlobeIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  createBrowserTask,
  deleteBrowserTask,
  saveBrowserTask,
  useBrowserTask,
  useBrowserTasks,
} from "../../state/browserTasks";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Textarea } from "../ui/textarea";
import {
  browserTaskFormDiffers,
  browserTaskFormFrom,
  checkBrowserTaskForm,
  domainCovers,
  emptyBrowserTaskForm,
  parseAllowedDomains,
  SCOPE_LABEL,
  startUrlHost,
  type BrowserTaskForm,
} from "./BrowserTasks.logic";

function Field({ label, hint, children }: { readonly label: string; readonly hint?: ReactNode; readonly children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium">{label}</span>
      {children}
      {hint ? <span className="block text-[11px] leading-snug text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

export function BrowserTaskBadges({ task }: { readonly task: Pick<BrowserTaskSummary, "scope" | "status"> }) {
  return (
    <>
      <Badge variant="outline" size="sm">
        {SCOPE_LABEL[task.scope]}
      </Badge>
      {task.status === "draft" ? (
        <Badge variant="warning" size="sm" data-browser-task-draft="">
          Draft
        </Badge>
      ) : null}
    </>
  );
}

const SCOPE_CHOICES: ReadonlyArray<{ readonly value: BrowserTaskScope; readonly label: string }> = [
  { value: "personal", label: "Just me" },
  { value: "organization", label: "Whole organisation" },
];

/** The form itself; `readOnly` shows a task the viewer may not change. */
function BrowserTaskFields({
  form,
  onChange,
  readOnly,
}: {
  readonly form: BrowserTaskForm;
  readonly onChange: (form: BrowserTaskForm) => void;
  readonly readOnly: boolean;
}) {
  const set = <K extends keyof BrowserTaskForm>(key: K, value: BrowserTaskForm[K]) => onChange({ ...form, [key]: value });
  const host = startUrlHost(form.startUrl);
  const domains = parseAllowedDomains(form.allowedDomains);
  const startUncovered = host !== null && !domainCovers(domains, host);
  return (
    <div className="space-y-3">
      <Field label="Name">
        <Input
          value={form.name}
          maxLength={200}
          readOnly={readOnly}
          placeholder="FCA final notices this week"
          onChange={(event) => set("name", event.currentTarget.value)}
          data-browser-task-name=""
        />
      </Field>
      <Field label="What it is for">
        <Textarea
          rows={2}
          value={form.purpose}
          maxLength={2_000}
          readOnly={readOnly}
          placeholder="This week's FCA final notices as PDFs, for the Monday review"
          onChange={(event) => set("purpose", event.currentTarget.value)}
        />
      </Field>
      <Field label="Start page">
        <Input
          value={form.startUrl}
          maxLength={2_000}
          readOnly={readOnly}
          placeholder="https://www.fca.org.uk/news"
          onChange={(event) => set("startUrl", event.currentTarget.value)}
        />
      </Field>
      <Field label="Steps" hint="In plain words, one step per line, with what to look for on each page.">
        <Textarea
          rows={7}
          value={form.steps}
          maxLength={10_000}
          readOnly={readOnly}
          placeholder={"1. Filter the news list to Final notices.\n2. Open each notice from the last 7 days.\n3. Download its PDF."}
          onChange={(event) => set("steps", event.currentTarget.value)}
        />
      </Field>
      <Field label="It worked when">
        <Textarea
          rows={2}
          value={form.successCheck}
          maxLength={2_000}
          readOnly={readOnly}
          placeholder="Every notice from the last 7 days has been downloaded"
          onChange={(event) => set("successCheck", event.currentTarget.value)}
        />
      </Field>
      <Field label="Files it produces">
        <Input
          value={form.produces}
          maxLength={1_000}
          readOnly={readOnly}
          placeholder="One PDF per notice"
          onChange={(event) => set("produces", event.currentTarget.value)}
        />
      </Field>
      <Field
        label="Sites it may visit"
        hint={
          startUncovered && !readOnly ? (
            <>
              The start page's site is not listed.{" "}
              <button
                type="button"
                className="font-medium text-foreground underline-offset-2 hover:underline"
                onClick={() => set("allowedDomains", [...domains, host].join("\n"))}
              >
                Add {host}
              </button>
            </>
          ) : (
            "One per line, like fca.org.uk, or *.gov.uk for every site under it. It never leaves these."
          )
        }
      >
        <Textarea
          rows={3}
          value={form.allowedDomains}
          readOnly={readOnly}
          placeholder={"www.fca.org.uk\n*.gov.uk"}
          onChange={(event) => set("allowedDomains", event.currentTarget.value)}
          data-browser-task-domains=""
        />
      </Field>
      <Field label="Shared with">
        <Select
          value={form.scope}
          disabled={readOnly}
          onValueChange={(value) => set("scope", value === "organization" ? "organization" : "personal")}
        >
          <SelectTrigger size="sm" aria-label="Shared with">
            <span className="truncate text-xs">{SCOPE_CHOICES.find((choice) => choice.value === form.scope)?.label}</span>
          </SelectTrigger>
          <SelectContent>
            {SCOPE_CHOICES.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Runs in your own chat with your saved logins. Reads and downloads only; up to 40 browser actions and 30 minutes.
      </p>
    </div>
  );
}

/** Edits (or shows) one stored task. */
function StoredTaskEditor({
  organizationId,
  task,
  onDone,
}: {
  readonly organizationId: string;
  readonly task: BrowserTask;
  readonly onDone: () => void;
}) {
  const [form, setForm] = useState<BrowserTaskForm>(() => browserTaskFormFrom(task));
  // The version this form was edited from; a newer save on the server is a conflict.
  const [version, setVersion] = useState(task.version);
  const [isDraft, setIsDraft] = useState(task.status === "draft");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const readOnly = !task.canEdit;
  const check = checkBrowserTaskForm(form);
  const changed = browserTaskFormDiffers(form, task) || version !== task.version;

  const save = async () => {
    if (!check.ok || busy) return;
    setBusy(true);
    setError(null);
    const outcome = await saveBrowserTask(organizationId, task.id, {
      definition: check.definition,
      ...(form.scope !== task.scope ? { scope: form.scope } : {}),
      ...(isDraft ? { status: "active" as const } : {}),
      expectedVersion: version,
    });
    setBusy(false);
    if (outcome.kind === "saved") {
      onDone();
      return;
    }
    setError(outcome.message);
    if (outcome.kind === "conflict" && outcome.latest) {
      // Their version replaces the form; saving again saves over it, knowingly.
      setForm(browserTaskFormFrom(outcome.latest));
      setVersion(outcome.latest.version);
      setIsDraft(outcome.latest.status === "draft");
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    const outcome = await deleteBrowserTask(organizationId, task.id);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      setConfirmingDelete(false);
      return;
    }
    onDone();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          {task.name}
          <BrowserTaskBadges task={{ scope: task.scope, status: isDraft ? "draft" : "active" }} />
        </DialogTitle>
        <DialogDescription>
          {readOnly
            ? task.scope === "organization"
              ? "The organisation's task. Only an admin can change it."
              : "Only its owner can change this task."
            : isDraft
              ? "Drafted from a chat. Check each field, then save it to use it in workflows."
              : `Version ${task.version}${task.ownerEmail ? ` · ${task.ownerEmail}` : ""}. A run keeps the version it started with.`}
        </DialogDescription>
      </DialogHeader>
      <DialogPanel className="space-y-3">
        <BrowserTaskFields form={form} onChange={setForm} readOnly={readOnly} />
        {!readOnly && !check.ok ? <p className="text-xs text-muted-foreground">{check.error}</p> : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter>
        {readOnly ? (
          <DialogClose render={<Button size="sm" variant="ghost">Close</Button>} />
        ) : confirmingDelete ? (
          <>
            <span className="mr-auto self-center text-sm text-foreground">Delete this browser task? Workflows using it will stop at its step.</span>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" disabled={busy} onClick={() => void remove()} data-browser-task-delete-confirm="">
              {busy ? "Deleting…" : "Delete task"}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              disabled={busy}
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2Icon />
              Delete
            </Button>
            <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
            <Button
              size="sm"
              disabled={busy || !check.ok || (!isDraft && !changed)}
              onClick={() => void save()}
              data-browser-task-save=""
            >
              {busy ? "Saving…" : isDraft ? "Save and use" : "Save"}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}

/** A new personal task (or the organization's, for an admin). */
function NewTaskEditor({
  organizationId,
  onCreated,
}: {
  readonly organizationId: string;
  readonly onCreated: (task: BrowserTask) => void;
}) {
  const [form, setForm] = useState<BrowserTaskForm>(emptyBrowserTaskForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = checkBrowserTaskForm(form);
  const create = async () => {
    if (!check.ok || busy) return;
    setBusy(true);
    setError(null);
    const outcome = await createBrowserTask(organizationId, { scope: form.scope, definition: check.definition });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onCreated(outcome.value);
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle>New browser task</DialogTitle>
        <DialogDescription>
          Write down a browser job once; a workflow's Browser task step can then do it again. Asking the browser helper in chat can draft one for you.
        </DialogDescription>
      </DialogHeader>
      <DialogPanel className="space-y-3">
        <BrowserTaskFields form={form} onChange={setForm} readOnly={false} />
        {!check.ok ? <p className="text-xs text-muted-foreground">{check.error}</p> : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter>
        <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
        <Button size="sm" disabled={busy || !check.ok} onClick={() => void create()} data-browser-task-create="">
          {busy ? "Creating…" : "Create task"}
        </Button>
      </DialogFooter>
    </>
  );
}

function StoredTask({
  organizationId,
  taskId,
  onDone,
}: {
  readonly organizationId: string;
  readonly taskId: string;
  readonly onDone: () => void;
}) {
  const { read } = useBrowserTask(organizationId, taskId);
  if (read?.kind === "ready") {
    return <StoredTaskEditor key={read.value.id} organizationId={organizationId} task={read.value} onDone={onDone} />;
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>Browser task</DialogTitle>
        <DialogDescription>
          {read === null
            ? "Loading…"
            : read.kind === "missing"
              ? "This browser task was deleted, or is someone else's."
              : "The browser task could not be loaded. Try again."}
        </DialogDescription>
      </DialogHeader>
      {read === null ? (
        <DialogPanel>
          <ListSkeleton rows={3} />
        </DialogPanel>
      ) : null}
    </>
  );
}

/**
 * One browser task in a dialog: `taskId` opens a stored one, null a new one.
 * `onCreated` hears of a new task (a step can then choose it).
 */
export function BrowserTaskDialog({
  organizationId,
  taskId,
  open,
  onOpenChange,
  onCreated,
}: {
  readonly organizationId: string;
  readonly taskId: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated?: (task: BrowserTask) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-2xl" data-browser-task-dialog="">
        {!open ? null : taskId ? (
          <StoredTask key={taskId} organizationId={organizationId} taskId={taskId} onDone={() => onOpenChange(false)} />
        ) : (
          <NewTaskEditor
            organizationId={organizationId}
            onCreated={(task) => {
              onCreated?.(task);
              onOpenChange(false);
            }}
          />
        )}
      </DialogPopup>
    </Dialog>
  );
}

function BrowserTaskRow({ task, onOpen }: { readonly task: BrowserTaskSummary; readonly onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        data-browser-task-row={task.id}
        className="group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left outline-none ring-ring transition-colors hover:bg-muted/50 focus-visible:ring-2"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          <GlobeIcon className="size-4.5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-foreground">{task.name}</span>
            <BrowserTaskBadges task={task} />
          </span>
          {task.purpose ? <p className="truncate text-sm text-muted-foreground">{task.purpose}</p> : null}
        </div>
        <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground max-sm:hidden" />
      </button>
    </li>
  );
}

/** The Workflows page's Browser tasks section: the viewer's tasks and the organization's. */
export function BrowserTasksSection({ organizationId }: { readonly organizationId: string }) {
  const { read } = useBrowserTasks(organizationId);
  const [opened, setOpened] = useState<{ readonly taskId: string | null } | null>(null);
  return (
    <section className="mt-12" data-browser-tasks="">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h2 className="text-base font-medium text-foreground">Browser tasks</h2>
          <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">
            Browser jobs written down once, for a workflow's Browser task step to do again. Each runs in your own chat with your saved logins.
          </p>
        </div>
        <Button size="sm" variant="outline" className="cursor-pointer rounded-full" onClick={() => setOpened({ taskId: null })} data-browser-task-new="">
          <PlusIcon />
          New browser task
        </Button>
      </div>
      {read === null ? (
        <div className="mt-4">
          <ListSkeleton rows={2} />
        </div>
      ) : read.kind !== "ready" ? (
        <p className="mt-4 text-sm text-muted-foreground">Browser tasks could not be loaded. Reload the page to try again.</p>
      ) : read.value.length === 0 ? (
        <p className="mt-4 rounded-xl border border-dashed border-border/60 px-6 py-8 text-center text-sm text-muted-foreground">
          No browser tasks yet. After the browser helper does a job in chat, ask it to save it as a browser task, or add one here.
        </p>
      ) : (
        <ul className="mt-4 space-y-1" data-browser-tasks-list="">
          {read.value.map((task) => (
            <BrowserTaskRow key={task.id} task={task} onOpen={() => setOpened({ taskId: task.id })} />
          ))}
        </ul>
      )}
      <BrowserTaskDialog
        organizationId={organizationId}
        taskId={opened?.taskId ?? null}
        open={opened !== null}
        onOpenChange={(open) => (open ? undefined : setOpened(null))}
      />
    </section>
  );
}
